import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { type FormEvent, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/Table';
import { listCategories, listProducts } from '../../data/fetchers';
import { upsertCategory } from '../../data/mutators';
import { canEditMasterData } from '../../lib/permissions';
import { useSession } from '../../stores/session';

interface CatRow {
  id: string;
  name: string;
  skus: number;
}

export function CategoriesView() {
  const qc = useQueryClient();
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditMasterData(user) : false;
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });

  const mutation = useMutation({
    mutationFn: upsertCategory,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] });
    },
  });

  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const onAdd = (e: FormEvent) => {
    e.preventDefault();
    if (!newId.trim() || !newName.trim()) return;
    mutation.mutate({ id: newId.trim(), name: newName.trim() });
    setNewId('');
    setNewName('');
  };
  const saveName = (id: string, name: string, prev: string) => {
    const next = name.trim();
    if (next && next !== prev) mutation.mutate({ id, name: next });
  };

  const rows = useMemo<CatRow[]>(
    () =>
      categories.map((c) => ({
        id: c.id,
        name: c.name,
        skus: products.filter((p) => p.categoryId === c.id).length,
      })),
    [categories, products],
  );

  const columns = useMemo<ColumnDef<CatRow, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'หมวดหมู่',
        cell: (c) =>
          canEdit ? (
            <Input
              size="sm"
              aria-label={`ชื่อหมวดหมู่ ${c.row.original.id}`}
              defaultValue={c.getValue<string>()}
              onBlur={(e) =>
                saveName(
                  c.row.original.id,
                  e.target.value,
                  c.getValue<string>(),
                )
              }
              className="w-full"
            />
          ) : (
            c.getValue<string>()
          ),
      },
      { accessorKey: 'id', header: 'รหัส' },
      {
        accessorKey: 'skus',
        header: 'จำนวน SKU',
        cell: (c) => <span className="num">{c.getValue<number>()}</span>,
      },
    ],
    [canEdit],
  );

  return (
    <div className="bo-fade">
      <PageHeader title="หมวดหมู่" subtitle={`${categories.length} หมวดหมู่`} />
      {canEdit && (
        <Card title="เพิ่มหมวดหมู่" className="mb-4">
          <form onSubmit={onAdd} className="flex gap-3 items-end">
            <Field label="รหัส" className="w-32">
              <Input
                aria-label="รหัสหมวดหมู่"
                value={newId}
                onChange={(e) => setNewId(e.target.value)}
              />
            </Field>
            <Field label="ชื่อ" className="flex-1">
              <Input
                aria-label="ชื่อหมวดหมู่ใหม่"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </Field>
            <Btn kind="primary" type="submit">
              เพิ่ม
            </Btn>
          </form>
        </Card>
      )}
      <DataTable columns={columns} data={rows} />
    </div>
  );
}
